/* eslint-disable react-hooks/exhaustive-deps */
import { getUpdatedStepData } from './utils';
import { NotificationStepsProps } from '../NotificationForm';
import { notificationBasicDetailsValidator } from '../notification-validators';
import { OktaUserInfo } from '@/app/core/auth';
import { Select } from '@ssar-app/uikit';
import {
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Text,
  VStack,
  Textarea,
  FormHelperText,
} from '@chakra-ui/react';
import { Controller, useForm } from 'react-hook-form';
import {
  Notification,
  NotificationType,
  defaultMaintenanceMsg,
  isSuperAdmin,
  getProductList,
} from '@ssar-app/domain';
import { useEffect, useState } from 'react';
import { yupResolver } from '@hookform/resolvers';

export const BasicDetails = (props: NotificationStepsProps) => {
  const {
    notification,
    setNotification,
    stepOptions,
    setStepOptions,
    triggerStepValidation,
  } = props;
  const [notifyMessage, setNotifyMessage] = useState<string>(
    notification.description,
  );
  const [notificationType, setNotificationType] = useState<string>(
    notification.type,
  );
  const userProfile = OktaUserInfo.getOktaUserInfo();
  const superAdmin = isSuperAdmin(userProfile);
  const productsList = getProductList(userProfile);
  const {
    errors,
    control,
    trigger,
    register,
    setValue,
    getValues,
    clearErrors,
  } = useForm<Notification>({
    defaultValues: notification,
    resolver: yupResolver(notificationBasicDetailsValidator, {
      abortEarly: false,
    }),
    mode: 'all',
    reValidateMode: 'onChange',
    criteriaMode: 'all',
  });

  triggerStepValidation.current = () =>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    trigger(Object.keys(getValues()) as any[]);

  useEffect(() => {
    register('type', { required: true });
    register('title', { required: true });
    register('description');
  }, []);

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      setStepOptions(getUpdatedStepData(stepOptions, 1, true));
    } else {
      setStepOptions(getUpdatedStepData(stepOptions, 1, false));
    }
  }, [Object.keys(errors).length]);

  useEffect(() => {
    setValue('description', notifyMessage);
  }, [notifyMessage]);

  useEffect(() => {
    setNotification({ ...notification, ...getValues() });
  }, [
    getValues().title,
    getValues().type,
    getValues().productName,
    getValues().description,
  ]);

  return (
    <Flex direction='column' minW='sm' mr='6' w='full'>
      <VStack alignItems='start' mb='6'>
        <Text fontSize='lg' fontWeight='semibold'>
          Step 1: Notification Details
        </Text>
        <Text fontSize='sm'>
          You can add notification type, title and message here.
        </Text>
      </VStack>
      <Flex direction={'row'} gap={36}>
        <FormControl isInvalid={!!errors.type} mb={6}>
          <FormLabel fontSize='sm' fontWeight={'semibold'}>
            Notification Type
          </FormLabel>
          <Controller
            name='type'
            control={control}
            defaultValue={notification.type}
            render={({ value, onChange }) => (
              <Select
                selectContainerProps={{ width: 200 }}
                placeholder='Select Notification Type'
                onChange={(values) => {
                  onChange(values);
                  setValue('type', values as string);
                  setNotificationType(values as string);
                  if (values === NotificationType.ANNOUNCEMENT) {
                    setNotifyMessage('');
                    setValue('productName', productsList[0]);
                    setNotification({
                      ...notification,
                      details: { ...notification.details, duration: -1 },
                    });
                  } else {
                    setValue('productName', 'ALL');
                    setNotifyMessage(defaultMaintenanceMsg);
                    setNotification({
                      ...notification,
                      details: { ...notification.details, duration: 0 },
                    });
                  }
                  clearErrors(['title', 'description']);
                }}
                defaultValue={value}
                options={Object.values(
                  superAdmin
                    ? NotificationType
                    : [NotificationType.ANNOUNCEMENT],
                ).map((type) => ({
                  label: type,
                  value: type,
                }))}
                optionLabelProps={{ color: 'black' }}
                noOptionsText='No Notification Type Found'
                isDisabled={notification.id || !superAdmin ? true : false}
              />
            )}
          />
        </FormControl>

        {notificationType === NotificationType.ANNOUNCEMENT && (
          <FormControl isInvalid={!!errors.type} mb={6}>
            <FormLabel fontSize='sm' fontWeight={'semibold'}>
              Select Product
            </FormLabel>
            <Controller
              name='productName'
              control={control}
              defaultValue={notification.productName}
              render={({ value, onChange }) => (
                <Select
                  selectContainerProps={{ width: 200 }}
                  placeholder='Select Product'
                  onChange={(values) => {
                    onChange(values);
                    setValue('productName', values as string);
                  }}
                  defaultValue={value}
                  options={productsList.map((name) => ({
                    label: name as string,
                    value: name as string,
                  }))}
                  optionLabelProps={{ color: 'black' }}
                  noOptionsText='No Products Found'
                  isDisabled={
                    notification.id || productsList.length === 1 ? true : false
                  }
                />
              )}
            />
          </FormControl>
        )}
      </Flex>
      <FormControl isRequired isInvalid={!!errors.title}>
        <FormLabel fontSize='sm' htmlFor='title' fontWeight={'semibold'}>
          Title
        </FormLabel>
        <Controller
          name='title'
          control={control}
          defaultValue={notification.title}
          render={({ value, onChange }) => (
            <Input
              maxLength={150}
              onChange={(e) => {
                onChange(e.target.value);
                trigger(['title']);
                setValue('title', e.target.value);
              }}
              value={value}
              placeholder='Enter a title'
              sx={{ borderColor: 'gray.300' }}
            />
          )}
        />
        <FormHelperText float={'right'}>
          {notification.title.length | 0}/150
        </FormHelperText>
        <FormErrorMessage>
          {errors?.title?.message?.replaceAll('title', 'Title')}
        </FormErrorMessage>
      </FormControl>
      <FormControl isRequired isInvalid={!!errors.description} mt={6} mb={6}>
        <FormLabel fontSize='sm' htmlFor='description' fontWeight={'semibold'}>
          Message
        </FormLabel>
        <Controller
          name='description'
          control={control}
          defaultValue={notifyMessage}
          render={({ value, onChange }) => (
            <Textarea
              maxLength={512}
              placeholder='Enter description'
              rows={2}
              onChange={(e) => {
                onChange(e.target.value);
                setNotifyMessage(e.target.value);
              }}
              value={value}
              color='black'
              borderColor='gray.500'
              fontSize={'sm'}
              disabled={notificationType === NotificationType.MAINTENANCE}
            />
          )}
        />
        <FormHelperText float={'right'}>
          {notification.description.length | 0}/512
        </FormHelperText>
        <FormErrorMessage>
          {errors.description?.message?.replaceAll(
            'description',
            'Desccription',
          )}
        </FormErrorMessage>
      </FormControl>
    </Flex>
  );
};

export default BasicDetails;
